import React, { useCallback, useEffect, useState } from 'react';
import { Link, useHistory, useLocation, useParams } from 'react-router-dom';
import * as Yup from 'yup';
import { doc, setDoc, getDoc, updateDoc } from 'firebase/firestore';
import { v4 } from 'uuid';
import compressor from 'browser-image-compression';
import { DB } from '~/services/firebase';
import { useAuth } from '~/hooks/auth';

import AWSBucket, { AWSParams } from '~/services/aws';

import Dashboard from '~/layouts/Dashboard';
import getValidationErrors from '~/utils/getValidationErrors';
// import ICompany from '~/interfaces/ICompany';
import { useToast } from '~/hooks/toast';
import { useCategoriesCompany } from '~/hooks/categoriesCompany';
import { bff } from '~/services/bff';
import { useSupplies } from '~/hooks/supplies';

interface FormData {
  uid: string;
  name: string;
  unit: string;
  stock: number;
  stock_min: number;
  status: boolean;
}

interface Params {
  id: string;
}

const SupplieForm: React.FC = () => {
  const history = useHistory();
  const params = useParams<Params>();
  const { state } = useLocation();
  const { user, company } = useAuth();
  const { addToast } = useToast();
  const { loadSupplies } = useSupplies();

  const [data, setData] = useState({} as FormData);
  const [errors, setErrors] = useState({} as any);
  const [loading, setLoading] = useState(false);
  const { id } = params;

  useEffect(() => {
    async function loadSupplie() {
      const suppliesRef = doc(DB, 'supplies', id);
      const docSnap = await getDoc(suppliesRef);
      setData({
        ...docSnap.data(),
        uid: id,
      } as FormData);
    }

    if (state) {
      setData(state as FormData);
    } else if (id) {
      loadSupplie();
    } else {
      setData({ status: true } as FormData);
    }
  }, [id, state]);

  const handleSubmit = useCallback(
    async event => {
      event.preventDefault();

      try {
        const schema = Yup.object().shape({
          name: Yup.string().required('O nome do insumo é obrigatório'),
          stock: Yup.string().required('O estoque é obrigatório'),
          stock_min: Yup.string().required('O estoque mínimo é obrigatório'),
        });

        await schema.validate(data, {
          abortEarly: false,
        });

        setLoading(true);

        const uid = data.uid || v4();

        const parseData: any = {
          ...data,
          unit: data.unit ? data.unit : 'UN',
          uid,
          user_id: user.uid,
          company_id: company.uid,
        };

        if (data.uid) {
          await updateDoc(doc(DB, 'supplies', uid), parseData);
        } else {
          await setDoc(doc(DB, 'supplies', uid), parseData);
        }

        loadSupplies(company.uid);

        // await bff.delete(`/supplies/${REACT_APP_FIREBASE_PROJECT}`);

        addToast({
          type: 'success',
          description: data.uid
            ? 'Insumo alterado com sucesso!'
            : 'Insumo criado com sucesso!',
        });

        history.push('/supplies');

        setLoading(false);
      } catch (err: any) {
        console.log(err);
        setLoading(false);
        if (err instanceof Yup.ValidationError) {
          const parseErrors = getValidationErrors(err);
          setErrors(parseErrors);
        }
      }
    },
    [user, company, data, addToast, history, loadSupplies],
  );

  return (
    <Dashboard containerMessage="col-xl-6 offset-xl-3 col-md-10 offset-md-1">
      <div className="row">
        <div className="col-xl-6 offset-xl-3 col-md-10 offset-md-1">
          <div className="nk-block nk-block-lg">
            <div className="nk-block-head">
              <div className="nk-block-head-content">
                <div className="nk-block-head-sub">
                  <Link className="back-to" to="/supplies">
                    <em className="icon ni ni-arrow-left" />
                    <span>Voltar</span>
                  </Link>
                </div>
                <h4 className="title nk-block-title">
                  {id ? 'Editar Insumo' : 'Criar Insumo'}
                </h4>
                <div className="nk-block-des">
                  <p>Preencha o formulário abaixo e crie um novo insumo.</p>
                </div>
              </div>
            </div>
            <div className="card card-bordered">
              <div className="card-inner">
                <form onSubmit={handleSubmit}>
                  <div className="row g-2">
                    <div className="col-lg-4">
                      <div className="form-group">
                        <label className="form-label">Nome do Insumo</label>
                        <div className="form-control-wrap">
                          <input
                            type="text"
                            className="form-control"
                            defaultValue={data.name}
                            onChange={event => {
                              setData({ ...data, name: event.target.value });
                            }}
                          />
                          {errors.name && (
                            <span className="invalid">{errors.name}</span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="col-sm-2">
                      <div className="form-group mb-3">
                        <label className="form-label">Unidade</label>
                        <div className="form-control-wrap ">
                          <div className="form-control-select">
                            <select
                              className="form-control"
                              value={data.unit}
                              onChange={event => {
                                setData({
                                  ...data,
                                  unit: event.target.value,
                                });
                              }}
                            >
                              <option value="UN">UN</option>
                              <option value="KG">KG</option>
                            </select>
                            {errors.unit && (
                              <span className="invalid">{errors.unit}</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="col-lg-2">
                      <div className="form-group">
                        <label className="form-label">Estoque Atual</label>
                        <div className="form-control-wrap">
                          <input
                            type="text"
                            className="form-control"
                            defaultValue={data.stock}
                            onChange={event => {
                              setData({
                                ...data,
                                stock: parseFloat(event.target.value),
                              });
                            }}
                          />
                          {errors.stock && (
                            <span className="invalid">{errors.stock}</span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="col-lg-2">
                      <div className="form-group">
                        <label className="form-label">Estoque Mínimo</label>
                        <div className="form-control-wrap">
                          <input
                            type="text"
                            className="form-control"
                            defaultValue={data.stock_min}
                            onChange={event => {
                              setData({
                                ...data,
                                stock_min: parseFloat(event.target.value),
                              });
                            }}
                          />
                          {errors.stock_min && (
                            <span className="invalid">{errors.stock_min}</span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="col-lg-2">
                      <div className="form-group">
                        <label className="form-label">Status?</label>
                        <ul className="custom-control-group g-3 align-center">
                          <li>
                            <div className="custom-control custom-control-sm custom-checkbox">
                              <input
                                id="active"
                                type="checkbox"
                                className="custom-control-input"
                                defaultChecked={data.status}
                                onClick={() => {
                                  setData({
                                    ...data,
                                    status: !data.status,
                                  });
                                }}
                              />
                              <label
                                className="custom-control-label"
                                htmlFor="active"
                              >
                                Ativo
                              </label>
                            </div>
                          </li>
                        </ul>
                      </div>
                    </div>

                    <div className="col-12">
                      <div className="form-group">
                        <button
                          type="submit"
                          className="btn btn-lg btn-primary"
                          disabled={loading}
                        >
                          {loading && (
                            <div
                              className="spinner-border spinner-border-sm"
                              role="status"
                            />
                          )}
                          {!loading && 'Salvar'}
                        </button>
                      </div>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Dashboard>
  );
};

export default SupplieForm;
